//* Libraries imports
import React from "react";
import { Modal, Button } from "react-bootstrap";
import { motion, AnimatePresence, Variants } from "framer-motion";

//* Icons imports
import { FiAlertTriangle, FiTrash2, FiX } from "react-icons/fi";
import { Icon } from "./Icon";

//* Styles imports
import "./DeleteConfirmationModal.scss";

interface DeleteConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  itemName?: string;
  loading?: boolean;
  confirmText?: string;
  cancelText?: string;
}

/**
 * DeleteConfirmationModal component displays a confirmation modal for delete operations.
 * Follows the clean layout pattern of the application with modern animations.
 *
 * @param {boolean} show - Determines whether the modal is visible.
 * @param {() => void} onHide - Function to call when the modal is closed/canceled.
 * @param {() => void} onConfirm - Function to call when the delete action is confirmed.
 * @param {string} title - Optional custom title for the modal.
 * @param {string} message - Optional custom message for the modal.
 * @param {string} itemName - Optional name of the item being deleted.
 * @param {boolean} loading - Optional loading state for the confirm button.
 * @param {string} confirmText - Optional custom text for confirm button.
 * @param {string} cancelText - Optional custom text for cancel button.
 *
 * @returns {JSX.Element} The rendered delete confirmation modal component.
 */
export const DeleteConfirmationModal: React.FC<
  DeleteConfirmationModalProps
> = ({
  show,
  onHide,
  onConfirm,
  title = "Confirmar Exclusão",
  message,
  itemName,
  loading = false,
  confirmText = "Excluir",
  cancelText = "Cancelar",
}) => {
  const defaultMessage = itemName
    ? `Tem certeza que deseja excluir "${itemName}"? Esta ação não pode ser desfeita.`
    : "Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.";

  const finalMessage = message || defaultMessage;
  // Animation variants for the modal content
  const modalVariants: Variants = {
    hidden: { opacity: 0, scale: 0.9, y: -20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 30,
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  };

  const iconVariants: Variants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 25,
        delay: 0.1,
      },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            keyboard={false}
            className="delete-confirmation-modal"
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Modal.Header closeButton className="border-0 pb-2">
                <Modal.Title className="d-flex align-items-center gap-3 w-100">
                  <motion.div
                    variants={iconVariants}
                    initial="hidden"
                    animate="visible"                    className="d-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10 p-2"
                    style={{ minWidth: "48px", minHeight: "48px" }}
                  >
                    <Icon icon={FiAlertTriangle} size={24} className="text-danger" />
                  </motion.div>
                  <span className="text-dark fw-semibold">{title}</span>
                </Modal.Title>
              </Modal.Header>

              <Modal.Body className="pt-0 px-4 pb-3">
                <div className="text-muted fs-6 lh-base">{finalMessage}</div>

                {itemName && (
                  <div className="mt-3 p-3 bg-light rounded-3">
                    <small className="text-muted d-block mb-1">
                      Item a ser excluído:
                    </small>
                    <span className="fw-medium text-dark">{itemName}</span>
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer className="border-0 pt-0 px-4 pb-4">
                <div className="d-flex gap-3 w-100 justify-content-end">
                  <motion.div
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <Button
                      variant="outline-secondary"
                      onClick={onHide}
                      disabled={loading}                      className="px-4 py-2 d-flex align-items-center gap-2"
                    >
                      <Icon icon={FiX} size={16} />
                      {cancelText}
                    </Button>
                  </motion.div>

                  <motion.div
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <Button
                      variant="danger"
                      onClick={onConfirm}
                      disabled={loading}
                      className="px-4 py-2 d-flex align-items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                          <span>Excluindo...</span>
                        </>                      ) : (
                        <>
                          <Icon icon={FiTrash2} size={16} />
                          {confirmText}
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </Modal.Footer>
            </motion.div>
          </Modal>
        </>
      )}
    </AnimatePresence>
  );
};

export default DeleteConfirmationModal;
